import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import Downshift from 'downshift'
import PropTypes from 'prop-types'
import { addSpaceTypeFilter, removeSpaceTypeFilter, fetchSearchResultsIfNotBusy } from '../actions/search'
import { colors, fontFamilies } from '../../search-common/settings'
import ArrowThinDown from '../icons/ArrowThinDown'
import CheckMark from '../icons/CheckMark'
import { translate } from '../../modules/helper-functions'
import { media } from '../../search-common/helpers'

const Container = styled.div`
  width: 100%;
  position: relative;
  z-index: 7;
  ${media.tablet`
    max-width: 287px;
  `}
`

const Menu = styled.ul`
  max-height: 200px;
  overflow-y: auto;
  position: absolute;
  width: 100%;
  border: 1px solid ${colors.grayTone2};
  z-index: 10;
  `

const Item = styled.li`
  cursor: pointer;
  font-family: ${fontFamilies.aeonik};
  font-size: 14px;
  line-height: 1;
  min-height: 40px;
  padding: 12px;
  font-weight: bold;
  background-color: ${( props ) => props.isSelected || props.isActive ? colors.grayTone1 : colors.black};
  color: ${( props ) => props.isSelected || props.isActive ? colors.highlight : colors.white};

  svg {
    float: right;
    height: 14px;
    width: 14px;
  }
  `

const ToggleButton = styled.button`
  width: 100%;
  line-height: 34px;
  font-size: 14px;
  ${media.tablet`
    line-height: 40px;
    font-size: 16px;
  `}
  padding: 0 12px;
  background-color: transparent;
  color: ${colors.white};
  border: none;
  text-align: left;
  cursor: pointer;

  svg {
    float: right;
    height: calc( 1rem + 14px );
    width: calc( 1rem + 4px );
    padding: 12px 2px 2px;
    path {
      fill: ${colors.white};
    }
  }
`

const InputWrap = styled.div`
  display: flex;
  border: 1px solid ${colors.grayTone3};
`

const Label = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 14px;
`

/**
 * SpaceTypeFilter component.
 */
class SpaceTypeFilter extends Component {
  /**
   * @constructor
   * @param {any} props
   */
  constructor( props ) {
    super( props )

    this.handleChange = this.handleChange.bind( this )
    this.itemToString = this.itemToString.bind( this )
  }

  /**
   * Function handles the component state changes
   * by either adding or removing the space type filter.
   *
   * @param {string} option
   * @return {option}
   */
  handleChange( option ) {
    const { addFilter, removeFilter } = this.props
    if ( option ) {
      addFilter( option.value )
    } else {
      removeFilter()
    }
    return option
  }

  /**
   * Function returns a string representation
   * of the item.
   *
   * @param {string} item
   * @return {string}
   */
  itemToString( item ) {
    return item ? `${item.label}` : ''
  }

  /**
   * Render method.
   */
  render() {
    const { selectedItem, translations, types } = this.props
    return (
      <Downshift
        itemToString={this.itemToString}
        onChange={this.handleChange}
        selectedItem={selectedItem}>
        {({
          getRootProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          isOpen,
          getToggleButtonProps,
          highlightedIndex
        }) => (
            <Container {...getRootProps()}>
              <Label {...getLabelProps()}>{translate( translations, 'Property type' )}</Label>
              <InputWrap>
                <ToggleButton {...getToggleButtonProps()}>
                  {selectedItem ? this.itemToString( selectedItem ) : translate( translations, 'Select...' )}
                  <ArrowThinDown fill={colors.grayTone2} />
                </ToggleButton>
              </InputWrap>
              {isOpen ?
                <Menu {...getMenuProps()}>
                  {types.map(( type, index ) => (
                    <Item
                      key={type.value}
                      {...getItemProps({
                        item: type,
                        isSelected: selectedItem && selectedItem.value === type.value,
                        isActive: highlightedIndex === index
                      })}
                    >
                      {this.itemToString( type )}
                      {selectedItem && selectedItem.value === type.value ? <CheckMark fill={colors.highlight} /> : null}
                    </Item>
                  ))}
                  {types.length === 0 &&
                    <Item>{translate( translations, 'Change your search parameters to get more results' )}</Item>
                  }
                </Menu>
                : null
              }
            </Container>
          )}
      </Downshift>
    )
  }
}

SpaceTypeFilter.propTypes = {
  types: PropTypes.array.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  translations: PropTypes.object.isRequired,
  selectedItem: PropTypes.object
}

/**
 * Map the redux state to component props.
 *
 * @param {object} state
 * @return {object}
 */
const mapStateToProps = ( state ) => ({
  types: state.search.all_facets.space_types,
  selectedItem: getSelectedItem( state ),
  translations: state.translations
})

/**
 * Function maps the selected item from the
 * facets by the selected filter.
 *
 * @param {object} state
 */
const getSelectedItem = ( state ) => {
  const selectedFilter = state.search.filters.space_type
  const types = state.search.all_facets.space_types
  let selectedItem = null
  types.forEach(( type ) => {
    if ( type.value === selectedFilter ) {
      selectedItem = type
    }
  })
  return selectedItem
}

/**
 * Add dispatch functions to components props.
 *
 * @param {function} dispatch
 * @returns {object}
 */
const mapDispatchToProps = ( dispatch ) => {
  return {
    addFilter: ( filter ) => {
      dispatch( addSpaceTypeFilter( filter ))
      dispatch( fetchSearchResultsIfNotBusy())
    },
    removeFilter: () => {
      dispatch( removeSpaceTypeFilter())
      dispatch( fetchSearchResultsIfNotBusy())
    }
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)( SpaceTypeFilter )
